import React, { FC } from 'react';
import { Link } from 'react-router-dom';

import MetadataUtils, { CATEGORY_TYPE_PUBLISHER } from '../utils/utils-metadata';
import { ProductMetaData } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { ProductClass } from '../../store/constants';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';

interface PublisherLinkProps {
    productClass: ProductClass | undefined;
    loanFormatKey: string | null | undefined;
    publisher: string | undefined;
    language: string;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

function createPublisherLink({
    productClass,
    loanFormatKey,
    publisher,
    language,
    shopLanguage,
    isMultiLanguageShopEnabled,
    ageCategory
}: PublisherLinkProps) {
    return MetadataUtils.createMetaDataLink({
        productClass,
        filter: {
            publisher: { include: [publisher] }
        },
        extra: {
            loanFormatKey,
            publisher,
            language,
            categoryType: CATEGORY_TYPE_PUBLISHER
        },
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    });
}

interface SetPublisherImprintLinkParams {
    productClass: ProductClass | undefined;
    loanFormatKey: string | null | undefined;
    publisherImprint: string;
    language: string;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

function createPublisherImprintLink({
    productClass,
    loanFormatKey,
    publisherImprint,
    language,
    shopLanguage,
    isMultiLanguageShopEnabled,
    ageCategory
}: SetPublisherImprintLinkParams) {
    return MetadataUtils.createMetaDataLink({
        productClass,
        filter: {
            publisherImprint: { include: [publisherImprint] }
        },
        extra: { loanFormatKey, language },
        isMultiLanguageShopEnabled,
        shopLanguage,
        ageCategory
    });
}

type Props = {
    readonly product: ProductMetaData;
    readonly loanFormatKey?: string | null;
    readonly linked?: boolean;
    readonly shopLanguage: string | undefined;
};

export const ProductPublisher: FC<Props> = ({ product = {}, loanFormatKey, linked = true, shopLanguage }) => {
    const productClass = product.productClass;
    const publisher = product.publisher;
    const publisherImprint = product.publisherImprint;
    const language = useLanguage();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    if (product.publisher) {
        const publisherLink = linked
            ? createPublisherLink({
                  productClass,
                  loanFormatKey,
                  publisher,
                  language,
                  isMultiLanguageShopEnabled,
                  shopLanguage,
                  ageCategory: ageCategorySearchParam
              })
            : undefined;

        const publisherImprintLink =
            publisherImprint && linked
                ? createPublisherImprintLink({
                      productClass,
                      loanFormatKey,
                      publisherImprint,
                      language,
                      shopLanguage,
                      isMultiLanguageShopEnabled,
                      ageCategory: ageCategorySearchParam
                  })
                : undefined;

        return (
            <p>
                <span className="publisher-name">
                    {publisherLink ? <Link to={publisherLink}>{publisher}</Link> : null}
                </span>
                {publisherImprintLink ? <span className="hyphen"> - </span> : ''}
                <span className="publisher-imprint">
                    {publisherImprintLink ? <Link to={publisherImprintLink}>{publisherImprint}</Link> : null}
                </span>
            </p>
        );
    } else {
        return null;
    }
};

export default ProductPublisher;
